import React, { useEffect, useState } from 'react';
import { db } from '../../../utils/firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { Subscriber } from '../../../types';
import { Search, Filter, Download, Trash2, ChevronDown, Mail, MailX, Users, UserPlus, UserX } from 'lucide-react';

const SubscriberManager: React.FC = () => {
  const [subscribers, setSubscribers] = useState<(Subscriber & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const q = query(collection(db, 'subscribers'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const subscriberList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Subscriber & { id: string })[];
      setSubscribers(subscriberList);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (id: string) => {
    if (!confirm('Are you sure you want to unsubscribe this email?')) return;
    try {
      await updateDoc(doc(db, 'subscribers', id), {
        unsubscribedAt: serverTimestamp()
      });
      setSubscribers(prev => prev.map(s =>
        s.id === id ? { ...s, unsubscribedAt: new Date() } : s
      ));
    } catch (error) {
      console.error("Error unsubscribing:", error);
      alert("Failed to unsubscribe");
    }
  };

  const handleResubscribe = async (id: string) => {
    try {
      await updateDoc(doc(db, 'subscribers', id), {
        unsubscribedAt: null
      });
      setSubscribers(prev => prev.map(s =>
        s.id === id ? { ...s, unsubscribedAt: undefined } : s
      ));
    } catch (error) {
      console.error("Error resubscribing:", error);
      alert("Failed to resubscribe");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to permanently delete this subscriber? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'subscribers', id));
      setSubscribers(prev => prev.filter(s => s.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete subscriber");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Are you sure you want to permanently delete ${selectedIds.size} subscriber(s)? This cannot be undone.`)) return;

    try {
      await Promise.all(
        Array.from(selectedIds).map(id => deleteDoc(doc(db, 'subscribers', id)))
      );
      setSubscribers(prev => prev.filter(s => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Error bulk deleting:", error);
      alert("Failed to delete some subscribers");
    }
  };

  const handleExportCSV = () => {
    setExporting(true);
    try {
      const headers = ['Email', 'Source', 'Subscribed Date', 'Status', 'Welcome Email Sent'];
      const rows = filteredSubscribers.map(s => [
        s.email,
        s.source,
        formatDate(s.createdAt),
        s.unsubscribedAt ? 'Unsubscribed' : 'Active',
        s.welcomeEmailSent ? 'Yes' : 'No'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error("Error exporting:", error);
      alert("Failed to export subscribers");
    } finally {
      setExporting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredSubscribers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSubscribers.map(s => s.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredSubscribers = subscribers.filter(sub => {
    const matchesSearch = sub.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = sourceFilter === 'all' || sub.source === sourceFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && !sub.unsubscribedAt) ||
      (statusFilter === 'unsubscribed' && sub.unsubscribedAt);
    return matchesSearch && matchesSource && matchesStatus;
  });

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  // Stats
  const totalActive = subscribers.filter(s => !s.unsubscribedAt).length;
  const totalUnsubscribed = subscribers.filter(s => s.unsubscribedAt).length;
  const recentSubscribers = subscribers.filter(s => {
    if (!s.createdAt) return false;
    const date = s.createdAt.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date >= weekAgo && !s.unsubscribedAt;
  }).length;

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{subscribers.length}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Subscribers</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded">
              <Mail size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalActive}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded">
              <UserPlus size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{recentSubscribers}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide">This Week</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded">
              <UserX size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalUnsubscribed}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Unsubscribed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm w-64"
            />
          </div>

          {/* Source Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Sources</option>
              <option value="footer">Footer</option>
              <option value="popup">Popup</option>
              <option value="event">Event</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-4 pr-8 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>
        </div>

        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-red-700 transition-colors"
            >
              <Trash2 size={16} />
              Delete ({selectedIds.size})
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-4 w-12">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredSubscribers.length && filteredSubscribers.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Email</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Source</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Subscribed</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Welcome Email</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Status</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredSubscribers.map(subscriber => (
              <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(subscriber.id)}
                    onChange={() => toggleSelect(subscriber.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="p-4 font-mono text-sm">{subscriber.email}</td>
                <td className="p-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-gray-100 text-gray-700 border-gray-200 uppercase tracking-wide">
                    {subscriber.source}
                  </span>
                </td>
                <td className="p-4 text-sm text-gray-600">{formatDate(subscriber.createdAt)}</td>
                <td className="p-4">
                  {subscriber.welcomeEmailSent ? (
                    <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                      <Mail size={14} /> Sent
                    </span>
                  ) : subscriber.welcomeEmailError ? (
                    <span className="inline-flex items-center gap-1 text-red-600 text-sm" title={subscriber.welcomeEmailError}>
                      <MailX size={14} /> Failed
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">Pending</span>
                  )}
                </td>
                <td className="p-4">
                  {subscriber.unsubscribedAt ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-red-100 text-red-800 border-red-200 uppercase tracking-wide">
                      Unsubscribed
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border bg-green-100 text-green-800 border-green-200 uppercase tracking-wide">
                      Active
                    </span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <div className="flex gap-2 justify-end">
                    {subscriber.unsubscribedAt ? (
                      <button
                        onClick={() => handleResubscribe(subscriber.id)}
                        className="text-xs font-bold uppercase tracking-wider px-3 py-1 border border-green-600 text-green-600 hover:bg-green-600 hover:text-white transition-colors"
                      >
                        Resubscribe
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnsubscribe(subscriber.id)}
                        className="text-xs font-bold uppercase tracking-wider px-3 py-1 border border-gray-300 text-gray-600 hover:border-black hover:text-black transition-colors"
                      >
                        Unsubscribe
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(subscriber.id)}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title="Delete permanently"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredSubscribers.length === 0 && (
              <tr>
                <td colSpan={7} className="p-12 text-center text-gray-500">
                  {subscribers.length === 0 ? "No subscribers yet." : "No subscribers match your filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer count */}
      <div className="text-sm text-gray-500">
        Showing {filteredSubscribers.length} of {subscribers.length} subscribers
      </div>
    </div>
  );
};

export default SubscriberManager;
